import { useCallback, useEffect, useRef, useState } from 'react';

export interface VirtualLinesConfig {
  lineCount: number;
  scrollerRef: React.RefObject<HTMLElement | null>;
  estimatedLineHeight: number;
  overscan?: number;
}

export interface VisibleRange {
  start: number; // 0-indexed inclusive
  end: number;   // 0-indexed exclusive
}

export interface VirtualLinesResult {
  visibleRange: VisibleRange;
  topSpacerHeight: number;
  bottomSpacerHeight: number;
  measureLine: (index: number, height: number) => void;
  getLineOffset: (index: number) => number;
  invalidate: () => void;
}

/**
 * Virtualized line rendering hook for the text viewer.
 *
 * Tracks measured line heights and uses estimated heights for unmeasured
 * lines. On scroll, calculates the visible range and spacer heights.
 * Only the lines in the visible range need to be rendered as DOM elements.
 */
export function useVirtualLines({
  lineCount,
  scrollerRef,
  estimatedLineHeight,
  overscan = 20,
}: VirtualLinesConfig): VirtualLinesResult {
  // Measured heights: index → px. Cleared on invalidate.
  const measuredRef = useRef(new Map<number, number>());

  // Force re-render trigger
  const [, setTick] = useState(0);
  const tick = useCallback(() => setTick((t) => t + 1), []);

  // Scroll state
  const [visibleRange, setVisibleRange] = useState<VisibleRange>({ start: 0, end: 0 });
  const [topSpacerHeight, setTopSpacerHeight] = useState(0);
  const [bottomSpacerHeight, setBottomSpacerHeight] = useState(0);

  const getHeight = useCallback(
    (index: number) => measuredRef.current.get(index) ?? estimatedLineHeight,
    [estimatedLineHeight],
  );

  /** Sum heights from startIdx (inclusive) to endIdx (exclusive). */
  const sumHeights = useCallback(
    (startIdx: number, endIdx: number) => {
      let total = 0;
      for (let i = startIdx; i < endIdx; i++) {
        total += getHeight(i);
      }
      return total;
    },
    [getHeight],
  );

  /** Get the top offset of a line by summing all preceding heights. */
  const getLineOffset = useCallback(
    (index: number) => sumHeights(0, Math.min(index, lineCount)),
    [sumHeights, lineCount],
  );

  /** Calculate visible range from current scroll position. */
  const recalc = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || lineCount === 0) {
      setVisibleRange({ start: 0, end: 0 });
      setTopSpacerHeight(0);
      setBottomSpacerHeight(0);
      return;
    }

    const scrollTop = el.scrollTop;
    const viewportHeight = el.clientHeight;

    // Find first visible line (binary search on cumulative heights)
    let cumulative = 0;
    let startIdx = 0;
    for (let i = 0; i < lineCount; i++) {
      const h = getHeight(i);
      if (cumulative + h > scrollTop) {
        startIdx = i;
        break;
      }
      cumulative += h;
      if (i === lineCount - 1) startIdx = lineCount;
    }

    // Find last visible line
    let endIdx = startIdx;
    let visibleCum = cumulative;
    for (let i = startIdx; i < lineCount; i++) {
      endIdx = i + 1;
      visibleCum += getHeight(i);
      if (visibleCum >= scrollTop + viewportHeight) break;
    }

    // Apply overscan
    const start = Math.max(0, startIdx - overscan);
    const end = Math.min(lineCount, endIdx + overscan);

    setVisibleRange({ start, end });
    setTopSpacerHeight(sumHeights(0, start));
    setBottomSpacerHeight(sumHeights(end, lineCount));
  }, [scrollerRef, lineCount, getHeight, sumHeights, overscan]);

  // Recalc on scroll
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => recalc();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollerRef, recalc]);

  // Recalc when lineCount changes (new chunks loaded) or on mount
  useEffect(() => {
    recalc();
  }, [lineCount, recalc]);

  // Recalc on container resize
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollerRef, recalc]);

  /** Record a line's measured height. Only triggers recalc if it changed. */
  const measureLine = useCallback(
    (index: number, height: number) => {
      const prev = measuredRef.current.get(index);
      if (prev !== undefined && Math.abs(prev - height) < 0.5) return;
      measuredRef.current.set(index, height);
      // Defer recalc to batch multiple measurements in the same frame
      requestAnimationFrame(() => recalc());
    },
    [recalc],
  );

  /** Clear all measured heights (font size / word wrap changed). */
  const invalidate = useCallback(() => {
    measuredRef.current.clear();
    recalc();
  }, [recalc]);

  return {
    visibleRange,
    topSpacerHeight,
    bottomSpacerHeight,
    measureLine,
    getLineOffset,
    invalidate,
  };
}
